import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {BulkSelector, Button, Detail, EMPHASIS, Heading} from '@forecast-it/design-system';
import {useSearchParams} from '../../../router/compat';
import {Link} from 'react-router-dom';
import {useTickets} from '../hooks/useTickets';
import {useDeleteTicket} from '../hooks/useDeleteTicket';
import {
	TicketListQuery,
	buildTicketListVariables,
	resolveMeAssignee,
	ME_SENTINEL,
	useTicketListFetchKey,
} from '../hooks/useTicketListQuery';
import type {useTicketListQuery_tickets$key} from '../hooks/__generated__/useTicketListQuery_tickets.graphql';
import type {useTicketListQueryQuery$data} from '../hooks/__generated__/useTicketListQueryQuery.graphql';
import {ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {useColumnVisibility} from '../hooks/useColumnVisibility';
import {useDefaultFilters} from '../hooks/useDefaultFilters';
import {useFilterPersistence} from '../hooks/useFilterPersistence';
import {useAuth} from '../../auth/context/AuthContext';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';
import {useMentionUsers, type MentionUser} from '../hooks/useMentionUsers';
import {useBulkAssign} from '../hooks/useBulkAssign';
import {useFilterDrawer, type DrawerFilterState} from '../hooks/useFilterDrawer';
import {TicketListSkeleton} from '../components/TicketListSkeleton';
import {TicketListEmpty} from '../components/TicketListEmpty';
import {TicketListTable} from '../components/TicketListTable';
import {ColumnsToggle} from '../components/ColumnsToggle';
import {LoadMoreButton} from '../components/LoadMoreButton';
import {ViewToggle, type TicketView} from '../components/ViewToggle';
import {BoardView} from '../components/board/BoardView';
import {BulkAssignModal} from '../components/BulkAssignModal';
import {CreateTicketModal} from '../components/CreateTicketModal';
import {DeleteTicketModal} from '../components/DeleteTicketModal';
import {GlobalSearch} from '../components/GlobalSearch';
import {FilterDrawer} from '../components/FilterDrawer';
import {QuickFilters, ANY_ASSIGNEE} from '../components/QuickFilters';
import type {ActiveFilters} from '../components/FilterBar';
import type {FilterOption} from '../constants/filterOptions';
import {UNASSIGNED_OPTION} from '../constants/filterOptions';
import type {ColumnKey} from '../constants/listColumns';
import type {Ticket, TicketPriority, TicketStatus} from '../types/ticket';
import {countActiveFilterFields} from '../utils/countActiveFilterFields';
import {hasMinRole} from '../../auth/utils/hasMinRole';
import {scopeBoardTickets} from '../utils/scopeBoardTickets';
import {slaPresentation} from '../utils/slaPresentation';
import {useEmbedded} from '../../../EmbeddedContext';
import {TICKET_PERMS, hasTicketPerm} from '../../auth/utils/ticketPerms';
import {useOpenCreateFromQuery} from './useOpenCreateFromQuery';

const ErrorBox = styled.div`
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.error};
	background: ${p => p.theme.sys.color.background.neutral['2']};
	padding: ${p => p.theme.sys.spacing[400]};
`;
const ErrorText = styled.p`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.semantic.error};
	margin: 0;
`;

const ListSection = styled.section`
	margin-top: ${p => p.theme.sys.spacing[400]};
`;
const AgentScopeBanner = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[300]};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['2']};
	padding: ${p => p.theme.sys.spacing[200]} ${p => p.theme.sys.spacing[400]};
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const LoadMoreWrap = styled.div`
	margin-top: ${p => p.theme.sys.spacing[400]};
	display: flex;
	justify-content: center;
`;
const PageMain = styled.main`
	padding: ${p => p.theme.sys.spacing[600]} ${p => p.theme.sys.spacing[800]};
`;
const HeaderRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	margin-bottom: ${p => p.theme.sys.spacing[500]};
	@media (min-width: 640px) {
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
	}
`;
const TitleBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const HeaderActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const QuickFilterRow = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[500]};
`;
const SettingsLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	padding: ${p => p.theme.sys.spacing[200]};
	color: ${p => p.theme.sys.color.text.content.auxiliary};
	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const SettingsIcon = styled.svg`
	width: 16px;
	height: 16px;
`;

interface ErrorBoundaryState {
	error: Error | null;
}

class TicketListErrorBoundary extends React.Component<{children: React.ReactNode; onRetry: () => void}, ErrorBoundaryState> {
	override state: ErrorBoundaryState = {error: null};
	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {error};
	}
	override render() {
		if (this.state.error) {
			return (
				<ErrorBox role="alert">
					<ErrorText>{this.state.error.message}</ErrorText>
					<Button
						emphasis={EMPHASIS.LOW}
						onPress={() => {
							this.setState({error: null});
							this.props.onRetry();
						}}
					>
						Retry
					</Button>
				</ErrorBox>
			);
		}
		return this.props.children;
	}
}

/** What the header's summary line reports about the loaded page of tickets. */
export interface QueueSummary {
	count: number;
	breaches: number;
}

export function summaryText({count, breaches}: QueueSummary): string {
	const tickets = `${count} ${count === 1 ? 'ticket' : 'tickets'}`;
	const sla = breaches > 0 ? `${breaches} SLA ${breaches === 1 ? 'breach' : 'breaches'}` : 'SLA on track';
	return `${tickets} · ${sla}`;
}

interface ContentProps {
	view: TicketView;
	queryRef: useTicketListQuery_tickets$key;
	/** Assignable people, fetched once by the route and shared with the pickers. */
	users: MentionUser[];
	onRefetch: () => void;
	onClearFilters: () => void;
	onSummaryChange: (summary: QueueSummary) => void;
	visibleColumns: ReadonlySet<ColumnKey>;
}

function TicketListContent({view, queryRef, users, onRefetch, onClearFilters, onSummaryChange, visibleColumns}: ContentProps) {
	const {user} = useAuth();
	const {canAssign, canManage} = useTicketPerms();

	const {items, hasNextPage, isLoadingNext, loadNext} = useTickets(queryRef);

	// Delete
	const [deleteTarget, setDeleteTarget] = useState<{id: string; slug: string} | null>(null);
	const {submit: deleteTicket, loading: deleting} = useDeleteTicket();

	const handleDeleteRequest = useCallback(
		(ticketId: string) => {
			const ticket = items.find(t => t.id === ticketId);
			setDeleteTarget({id: ticket?.globalId ?? ticketId, slug: ticket?.slug ?? ''});
		},
		[items]
	);

	const handleDeleteConfirm = useCallback(() => {
		if (!deleteTarget) return;
		deleteTicket(deleteTarget.id).then(
			() => {
				setDeleteTarget(null);
				onRefetch();
			},
			() => {
				setDeleteTarget(null);
			}
		);
	}, [deleteTarget, deleteTicket, onRefetch]);

	// Selection. Each status group is its own table and reports only its own rows,
	// so the groups are kept apart and flattened on read.
	const [selectionByStatus, setSelectionByStatus] = useState<Record<string, Ticket[]>>({});
	const [selectionEpoch, setSelectionEpoch] = useState(0);
	const selectedTickets = useMemo(
		() => Object.values(selectionByStatus).reduce<Ticket[]>((all, group) => all.concat(group), []),
		[selectionByStatus]
	);

	const handleSelectionChange = useCallback((status: TicketStatus, selected: Ticket[]) => {
		setSelectionByStatus(prev => ({...prev, [status]: selected}));
	}, []);

	const clearSelection = useCallback(() => {
		setSelectionByStatus({});
		setSelectionEpoch(epoch => epoch + 1);
	}, []);

	// Selection belongs to the list; drop it when the view changes.
	useEffect(() => {
		clearSelection();
	}, [view, clearSelection]);

	// Assignment. One flow covers the row action (a single ticket) and the bulk bar
	// (the selection), so both get the same picker and the same result summary.
	const [assignTargets, setAssignTargets] = useState<Ticket[] | null>(null);
	const {status: bulkStatus, results: bulkResults, assign, reset: resetBulk} = useBulkAssign();

	const handleAssignRequest = useCallback(
		(ticketId: string) => {
			const ticket = items.find(t => t.id === ticketId);
			if (ticket) setAssignTargets([ticket]);
		},
		[items]
	);

	const handleAssignConfirm = useCallback(
		(userId: string | null) => {
			if (!assignTargets) return;
			assign(
				assignTargets.map(ticket => ticket.id),
				userId
			);
		},
		[assignTargets, assign]
	);

	const handleAssignDone = useCallback(() => {
		setAssignTargets(null);
		clearSelection();
		resetBulk();
		onRefetch();
	}, [clearSelection, resetBulk, onRefetch]);

	// The header's summary line sits above the query boundary, so the loaded page
	// reports its own totals upwards.
	useEffect(() => {
		onSummaryChange({
			count: items.length,
			breaches: items.filter(ticket => slaPresentation(ticket.sla).state === 'breached').length,
		});
	}, [items, onSummaryChange]);

	return (
		<>
			<ListSection>
				{view === 'board' && user ? (
					<>
						{user.role === 'agent' && (
							<AgentScopeBanner role="status">Showing your assigned tickets only.</AgentScopeBanner>
						)}
						<BoardView
							tickets={scopeBoardTickets(items, user.role, user.id)}
							me={{sub: user.id, role: user.role}}
							canManage={canManage}
							hasMore={hasNextPage}
							onMutate={onRefetch}
							users={users}
						/>
					</>
				) : items.length === 0 ? (
					<TicketListEmpty onClearFilters={onClearFilters} />
				) : (
					<TicketListTable
						tickets={items}
						onAssign={canManage ? handleAssignRequest : undefined}
						onDelete={canManage ? handleDeleteRequest : undefined}
						onSelectionChange={canAssign ? handleSelectionChange : undefined}
						selectionEpoch={selectionEpoch}
						visibleColumns={visibleColumns}
					/>
				)}

				{view === 'list' && items.length > 0 && hasNextPage && (
					<LoadMoreWrap>
						<LoadMoreButton onClick={() => loadNext(25)} loading={isLoadingNext} />
					</LoadMoreWrap>
				)}
			</ListSection>

			{view === 'list' && canAssign && (
				<BulkSelector
					show={selectedTickets.length > 0}
					label={`${selectedTickets.length} selected`}
					onClear={clearSelection}
					data-cy="ticket-bulk-selector"
				>
					{/* The children have to be an ARRAY. BulkSelector splits them into main and
					    overflow actions with Array.prototype.reduce, and React does not wrap a
					    lone JSX child in an array — so a single action here throws
					    "children.reduce is not a function" the moment `show` turns true, taking
					    the whole list view down with it the first time a row is ticked. */}
					{[
						<BulkSelector.MainAction
							key="assign"
							onClick={() => setAssignTargets(selectedTickets)}
							data-cy="ticket-bulk-assign"
						>
							Assign selected
						</BulkSelector.MainAction>,
					]}
				</BulkSelector>
			)}

			{assignTargets && (
				<BulkAssignModal
					ticketCount={assignTargets.length}
					users={users}
					loading={bulkStatus === 'loading'}
					results={bulkStatus === 'done' ? bulkResults : undefined}
					onConfirm={handleAssignConfirm}
					onCancel={() => {
						setAssignTargets(null);
						resetBulk();
					}}
					onDone={handleAssignDone}
				/>
			)}

			<DeleteTicketModal
				open={deleteTarget !== null}
				ticketSlug={deleteTarget?.slug ?? ''}
				loading={deleting}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteTarget(null)}
			/>
		</>
	);
}

export function TicketListRoute() {
	const [searchParams, setSearchParams] = useSearchParams();
	const {user} = useAuth();
	const {canCreate} = useTicketPerms();
	const {users} = useMentionUsers();
	const embedded = useEmbedded();
	const canAdminTickets = hasTicketPerm(user?.ticketPerms ?? [], TICKET_PERMS.ADMIN);
	const canFilterByAnyAssignee = user ? hasMinRole(user.role, 'lead') : false;

	useDefaultFilters();
	useFilterPersistence();

	const viewParam = searchParams.get('view');
	const view: TicketView = viewParam === 'board' ? 'board' : 'list';

	useEffect(() => {
		if (view !== 'board' || !searchParams.has('status')) return;
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				return updated;
			},
			{replace: true}
		);
	}, [searchParams, setSearchParams, view]);

	const filters: ActiveFilters = {
		status: searchParams.getAll('status'),
		priority: searchParams.getAll('priority'),
		sla_status: searchParams.getAll('sla_status'),
		assignee_id: searchParams.getAll('assignee_id'),
		label_id: searchParams.getAll('label_id'),
		requester_id: searchParams.getAll('requester_id'),
		created_after: searchParams.get('created_after') ?? '',
		created_before: searchParams.get('created_before') ?? '',
		updated_after: searchParams.get('updated_after') ?? '',
		updated_before: searchParams.get('updated_before') ?? '',
	};

	const activeFilterCount = countActiveFilterFields(filters);

	const sortField = searchParams.get('sort') ?? undefined;
	const sortOrder = (searchParams.get('order') ?? undefined) as 'asc' | 'desc' | undefined;
	const search = searchParams.get('search') ?? undefined;

	// The quick filters are single-select: a queue is read one slice at a time.
	// Anything the drawer set beyond one value therefore reads as "All" here rather
	// than misreporting a multi-select as a single pick.
	const quickPriority = (filters.priority.length === 1 ? filters.priority[0] : null) as TicketPriority | null;
	const quickAssignee = filters.assignee_id.length === 1 ? filters.assignee_id[0]! : ANY_ASSIGNEE;
	const quickFiltersActive = quickPriority !== null || quickAssignee !== ANY_ASSIGNEE;

	const assigneeOptions = useMemo<FilterOption[]>(() => {
		const options: FilterOption[] = [];
		if (canFilterByAnyAssignee) options.push({id: ANY_ASSIGNEE, label: 'Anyone'});
		options.push({id: ME_SENTINEL, label: 'Me'});
		options.push(UNASSIGNED_OPTION);
		if (canFilterByAnyAssignee) {
			for (const person of users) {
				options.push({id: person.id, label: person.name});
			}
		}
		return options;
	}, [canFilterByAnyAssignee, users]);

	function setQuickFilter(next: {priority?: TicketPriority | null; assignee?: string}) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				const priority = next.priority === undefined ? quickPriority : next.priority;
				const assignee = next.assignee === undefined ? quickAssignee : next.assignee;

				updated.delete('priority');
				updated.delete('assignee_id');
				if (priority) updated.append('priority', priority);
				if (assignee !== ANY_ASSIGNEE) updated.append('assignee_id', assignee);

				// A quick pick supersedes the drawer, so the drawer's marker is dropped
				// and the view leaves the role-based default set.
				updated.delete('filter_source');
				if (updated.get('view') !== 'board') {
					updated.set('view', 'all');
				}
				return updated;
			},
			{replace: true}
		);
	}

	function handleClearFilters() {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				updated.delete('priority');
				updated.delete('sla_status');
				updated.delete('assignee_id');
				updated.delete('label_id');
				updated.delete('requester_id');
				updated.delete('created_after');
				updated.delete('created_before');
				updated.delete('updated_after');
				updated.delete('updated_before');
				updated.delete('search');
				updated.delete('filter_source');
				updated.set('view', 'all');
				return updated;
			},
			{replace: true}
		);
	}

	function handleViewChange(next: TicketView) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				if (next === 'board') {
					updated.set('view', 'board');
					updated.delete('status');
				} else {
					updated.delete('view');
				}
				return updated;
			},
			{replace: true}
		);
	}

	const {fetchKey, refetch} = useTicketListFetchKey();

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [summary, setSummary] = useState<QueueSummary>({count: 0, breaches: 0});

	// Opens the create modal when arrived at via the top-nav "Create → Ticket" item.
	useOpenCreateFromQuery(canCreate, () => setShowCreateModal(true));

	// refetchOnFocus for board view
	useEffect(() => {
		if (view !== 'board') return;
		const onFocus = () => refetch();
		const onVisible = () => {
			if (document.visibilityState === 'visible') refetch();
		};
		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [view, refetch]);

	const columns = useColumnVisibility();

	const drawer = useFilterDrawer();
	const [savedFiltersFetchKey, setSavedFiltersFetchKey] = useState(0);

	function handleOpenDrawer() {
		drawer.open({
			status: filters.status,
			priority: filters.priority,
			sla_status: filters.sla_status,
			assignee_id: filters.assignee_id,
			label_id: filters.label_id,
			requester_id: filters.requester_id,
			created_after: filters.created_after,
			created_before: filters.created_before,
			updated_after: filters.updated_after,
			updated_before: filters.updated_before,
		});
	}

	function handleDrawerApply(draft: DrawerFilterState) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				updated.delete('priority');
				updated.delete('sla_status');
				updated.delete('assignee_id');
				updated.delete('label_id');
				updated.delete('requester_id');
				updated.delete('created_after');
				updated.delete('created_before');
				updated.delete('updated_after');
				updated.delete('updated_before');

				draft.status.forEach(v => updated.append('status', v));
				draft.priority.forEach(v => updated.append('priority', v));
				draft.sla_status.forEach(v => updated.append('sla_status', v));
				draft.assignee_id.forEach(v => updated.append('assignee_id', v));
				draft.label_id.forEach(v => updated.append('label_id', v));
				draft.requester_id.forEach(v => updated.append('requester_id', v));
				if (draft.created_after) updated.set('created_after', draft.created_after);
				if (draft.created_before) updated.set('created_before', draft.created_before);
				if (draft.updated_after) updated.set('updated_after', draft.updated_after);
				if (draft.updated_before) updated.set('updated_before', draft.updated_before);

				if (countActiveFilterFields(draft) > 0) {
					updated.set('filter_source', 'drawer');
				} else {
					updated.delete('filter_source');
				}

				if (updated.get('view') !== 'board') {
					updated.set('view', 'all');
				}

				return updated;
			},
			{replace: true}
		);
		drawer.close();
		refetch();
	}

	return (
		<PageMain>
			<HeaderRow>
				<TitleBlock>
					{/* The design-system Heading carries the heading TYPE on a div, so `as` is
					    what gives the page a real h1. */}
					<Heading size="1" as="h1">
						All tickets
					</Heading>
					<Detail color="medium" data-testid="queue-summary">
						{summaryText(summary)}
					</Detail>
				</TitleBlock>
				<HeaderActions>
					<GlobalSearch />
					<ViewToggle value={view} onChange={handleViewChange} />
					{canCreate && (
						<Button emphasis={EMPHASIS.HIGH} icon="plus" onPress={() => setShowCreateModal(true)}>
							New ticket
						</Button>
					)}
				</HeaderActions>
			</HeaderRow>

			<QuickFilterRow>
				<QuickFilters
					priority={quickPriority}
					onPriorityChange={priority => setQuickFilter({priority})}
					assignee={quickAssignee}
					assigneeOptions={assigneeOptions}
					onAssigneeChange={assignee => setQuickFilter({assignee})}
					hasFilters={quickFiltersActive}
					onClear={handleClearFilters}
					trailing={
						<>
							<Button
								emphasis={EMPHASIS.LOW}
								icon="filter"
								onPress={handleOpenDrawer}
								data-testid="open-filter-drawer"
								data-cy="open-filter-drawer"
							>
								{activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
							</Button>
							{view === 'list' && <ColumnsToggle isVisible={columns.isVisible} onToggle={columns.toggle} />}
							{!embedded && canAdminTickets && (
								<SettingsLink to="/tickets/settings" aria-label="Ticket settings" title="Ticket settings">
									<SettingsIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
										/>
										<circle cx="12" cy="12" r="3" />
									</SettingsIcon>
								</SettingsLink>
							)}
						</>
					}
				/>
			</QuickFilterRow>

			<TicketListErrorBoundary key={fetchKey} onRetry={refetch}>
				<ForecastQueryRenderer
					query={TicketListQuery}
					variables={buildTicketListVariables({
						first: view === 'board' ? 100 : 25,
						filters: {...filters, assignee_id: resolveMeAssignee(filters.assignee_id, user?.id)},
						sort: sortField,
						order: sortOrder,
						search,
					})}
					customLoader={() => <TicketListSkeleton />}
					redirectIfNotLoggedIn
					render={(relayProps: useTicketListQueryQuery$data | null) => {
						if (!relayProps || !relayProps.viewer) return null;
						return (
							<TicketListContent
								view={view}
								queryRef={relayProps.viewer}
								users={users}
								onRefetch={refetch}
								onClearFilters={handleClearFilters}
								onSummaryChange={setSummary}
								visibleColumns={columns.visible}
							/>
						);
					}}
				/>
			</TicketListErrorBoundary>

			<React.Suspense fallback={null}>
				<FilterDrawer
					isOpen={drawer.isOpen}
					draft={drawer.draft}
					onClose={drawer.close}
					onApply={handleDrawerApply}
					onUpdateDraft={drawer.updateDraft}
					onClearDraft={drawer.clearDraft}
					savedFiltersFetchKey={savedFiltersFetchKey}
					onSavedFiltersRefetch={() => setSavedFiltersFetchKey(k => k + 1)}
					canFilterByAssignee={canFilterByAnyAssignee}
				/>
			</React.Suspense>

			<CreateTicketModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onCreated={() => {
					setShowCreateModal(false);
					refetch();
				}}
			/>
		</PageMain>
	);
}
